import { useEffect, useRef, useState } from 'react'
import styles from './EditSkillModal.module.css'

// Mirrors the backend's bounds on skills.target_agent_count (catalog.py).
const MIN_TARGET = 1
const MAX_TARGET = 1000

/**
 * EditSkillModal — rename and/or reclassify an existing skill.
 *
 * Pure render/collect component: the page (or the Skill Details modal) owns the
 * skill-types list and the save call. Prefilled with the skill's current name and
 * type. Non-dismissable except via Cancel (no backdrop/Escape close) so an in-progress
 * edit isn't lost to a stray click, mirroring AddSkillModal.
 *
 * Props:
 *   currentName {string}        — the skill's existing name (prefilled into the input)
 *   currentType {string}        — the skill's existing type (prefilled selection)
 *   currentDescription {string} — the skill's existing description (prefilled textarea)
 *   currentTarget {number}      — the skill's current optimal coverage (people who should hold it)
 *   types {string[]}            — valid skill types for the dropdown
 *   busy  {boolean}             — disables the form while the save call is inflight
 *   error {string}              — error text to show in-dialog (the save failed; stays open)
 *   onSave {function}           — called with { skillName, skillType, description } plus
 *     { targetAgentCount } only when the optimal coverage was actually edited
 *   onCancel {function}
 */
export default function EditSkillModal({
  currentName = '', currentType = '', currentDescription = '', currentTarget = 10, types = [], busy = false,
  error = '', onSave, onCancel,
}) {
  const [skillName, setSkillName] = useState(currentName)
  const [skillType, setSkillType] = useState(currentType)
  const [description, setDescription] = useState(currentDescription)
  // Held as the raw input string so the field can be cleared/typed freely.
  const [target, setTarget] = useState(String(currentTarget))
  const firstFieldRef = useRef(null)

  useEffect(() => {
    firstFieldRef.current?.focus()
    firstFieldRef.current?.select()
  }, [])

  const trimmed = skillName.trim()
  const targetNum = Number(target)
  const targetValid = /^\d+$/.test(target.trim()) && targetNum >= MIN_TARGET && targetNum <= MAX_TARGET
  const targetChanged = targetValid && targetNum !== currentTarget
  const changed = trimmed !== currentName || skillType !== currentType
    || description !== currentDescription || targetChanged
  const canSave = trimmed !== '' && skillType !== '' && targetValid && changed && !busy

  function handleSave() {
    if (!canSave) return
    onSave({
      skillName: trimmed, skillType, description,
      // Only a real change goes out — an edit that doesn't touch the target never writes
      // it, so it can't clobber a change someone else made while this dialog was open.
      // When it is sent, the last save wins.
      ...(targetChanged && { targetAgentCount: targetNum }),
    })
  }

  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Edit skill">
        <h2 className={styles.title}>Edit Skill</h2>
        <p className={styles.message}>
          Rename this skill or change its classification. The change applies everywhere
          GaussIQ tracks the skill.
        </p>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Skill Name</span>
          <input
            ref={firstFieldRef}
            className={styles.input}
            type="text"
            value={skillName}
            onChange={e => setSkillName(e.target.value)}
            placeholder="e.g. Salesforce Apex debugging"
            disabled={busy}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Skill Type</span>
          <select
            className={styles.select}
            value={skillType}
            onChange={e => setSkillType(e.target.value)}
            disabled={busy}
          >
            {types.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Optimal Coverage</span>
          <input
            className={styles.input}
            type="number"
            inputMode="numeric"
            min={MIN_TARGET}
            max={MAX_TARGET}
            step={1}
            value={target}
            onChange={e => setTarget(e.target.value)}
            disabled={busy}
            aria-invalid={!targetValid}
          />
          {!targetValid && (
            <span className={styles.error}>
              Enter a whole number from {MIN_TARGET} to {MAX_TARGET}.
            </span>
          )}
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Description</span>
          <textarea
            className={styles.textarea}
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What this skill covers and when it applies"
            disabled={busy}
          />
        </label>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button className={styles.cancelBtn} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className={styles.saveBtn} onClick={handleSave} disabled={!canSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  )
}
